import { useState, useEffect, type FC } from "react";
import type { InvoiceAssessmentModel } from "../../../models/InvoiceAssessment";
import Subtitle from "../../atoms/typography/Subtitle";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import RadioButtonBox from "../../atoms/inputs/RadioButtonBox";
import Alert from "../Alert";
import TextField from "../../atoms/inputs/TextField";
import Select from "../../atoms/inputs/Select";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import Divider from "../../atoms/Divider";
import ActionButton from "../../atoms/buttons/ActionButton";
import { useLgEmailTemplates } from "../../../graphql/hooks/contentful/useLgEmailTemplates";
import { useRecipientApprovalPreview } from "../../../graphql/hooks/email/useRecipientApprovalPreview";
import {
  substituteTemplateVariables,
  templateContentToHtml,
} from "../../../utils/lgEmailTemplateUtils";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import ClientOrgInfo from "../client/ClientOrgInfo";
import { validateEmail } from "../../../utils/validation";
import BodySmall from "../../atoms/typography/BodySmall";

interface EmailParams {
  recipientEmail: string;
  message: string;
}

interface Props {
  selectedInvoices: Array<InvoiceAssessmentModel>;
  onClose: () => void;
  onSend: (params: EmailParams) => void;
  loading: boolean;
  errorMessage: string | null;
  successMessage: string | null;
  recipientClientIdentifier: string;
  issuerClientIdentifier: string;
}

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1.5px solid ${theme.colors.grey05};
  margin-bottom: 12px;
`;

const Tab = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2.5px solid
    ${({ active }) => (active ? theme.colors.primary : "transparent")};
  margin-bottom: -1.5px;
  padding: 6px 16px;
  cursor: pointer;
  font-weight: ${({ active }) => (active ? "600" : "400")};
  color: ${({ active }) =>
    active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: 14px;
  transition: color 0.15s;
`;

const HtmlPreviewContainer = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 8px;
  min-height: 500px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  iframe {
    width: 100%;
    min-height: 500px;
    border: none;
  }
`;

const SendLgModalContent: FC<Props> = ({
  selectedInvoices,
  onClose,
  onSend,
  loading,
  errorMessage,
  successMessage,
  recipientClientIdentifier,
  issuerClientIdentifier,
}) => {
  const { t } = useTranslation();
  const { lgTemplates } = useLgEmailTemplates();
  const [sendToRecipient, setSendToRecipient] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  //const [historyOpen, setHistoryOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const [customErrorMessage, setCustomErrorMessage] = useState<string | null>(
    null,
  );

  const { htmlContent, loading: previewLoading } = useRecipientApprovalPreview(
    templateContentToHtml(message),
    !showPreview,
  );

  const filteredTemplates = lgTemplates.filter(
    (t) => t.templateType === (sendToRecipient ? "recipient" : "issuer"),
  );

  useEffect(() => {
    setSelectedTemplate("");
    setMessage("");
  }, [sendToRecipient]);

  useEffect(() => {
    if (!selectedTemplate && filteredTemplates.length > 0) {
      onSelectTemplate(filteredTemplates[0].id);
    }
  }, [filteredTemplates]);

  const onSelectTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = filteredTemplates.find((t) => t.id === templateId);
    if (!template) return;
    const invoice = selectedInvoices[0];
    const substituted = invoice
      ? substituteTemplateVariables(template.content, invoice)
      : template.content;
    setMessage(substituted);
  };

  const handleSend = () => {
    if (!validateEmail(recipientEmail)) {
      setCustomErrorMessage(t("errors.invalidEmailInput"));
      return;
    }

    onSend({
      recipientEmail,
      message: templateContentToHtml(message),
    });
  };

  return (
    <div style={{ width: "800px" }}>
      <Subtitle color={theme.colors.primary}>
        {t("invoiceAssessment.deliveryApproval")}
      </Subtitle>
      <HorizontalSpacer spacing={1} />
      <div style={{ display: "flex", gap: 24 }}>
        <RadioButtonBox
          title={t("invoiceAssessment.sendToRecipient")}
          checked={sendToRecipient}
          onChange={() => setSendToRecipient(true)}
          small
        />
        <RadioButtonBox
          title={t("invoiceAssessment.sendToIssuer")}
          checked={!sendToRecipient}
          onChange={() => setSendToRecipient(false)}
          small
        />
      </div>
      <HorizontalSpacer spacing={1} />
      {/*<Alert
        type="issue"
        title={t("invoiceAssessment.emailDomainCreatedRecently")}
        small
      />

      <HorizontalSpacer spacing={1} />*/}
      <ClientOrgInfo
        clientIdentifier={
          sendToRecipient ? recipientClientIdentifier : issuerClientIdentifier
        }
      />

      <HorizontalSpacer spacing={1} />
      <TextField
        label="Till"
        value={recipientEmail}
        onChange={setRecipientEmail}
      />
      {customErrorMessage && (
        <BodySmall color={theme.colors.error}>{customErrorMessage}</BodySmall>
      )}
      <HorizontalSpacer spacing={1} />
      <Select
        label={t("invoiceAssessment.emailTemplate")}
        options={filteredTemplates.map((tmpl) => ({
          label: tmpl.title,
          value: tmpl.id,
        }))}
        value={selectedTemplate}
        onChange={onSelectTemplate}
      />
      <HorizontalSpacer spacing={1} />
      <TabBar>
        <Tab active={showPreview} onClick={() => setShowPreview(true)}>
          {t("common.preview")}
        </Tab>
        <Tab active={!showPreview} onClick={() => setShowPreview(false)}>
          {t("common.edit")}
        </Tab>
      </TabBar>
      {showPreview ? (
        <HtmlPreviewContainer>
          {previewLoading ? (
            <SmallLoadingSpinner />
          ) : htmlContent ? (
            <iframe
              srcDoc={htmlContent}
              title={t("invoiceAssessment.preview")}
              sandbox="allow-same-origin"
            />
          ) : null}
        </HtmlPreviewContainer>
      ) : (
        <MultilineTextField value={message} onChange={setMessage} rows={24} />
      )}

      <HorizontalSpacer spacing={1} />
      {/*<GreyContainer>
        <Row nogutter align="center" justify="between">
          <Subheading>{t("random.history")}</Subheading>
          <ChevronButton
            type={historyOpen ? "up" : "down"}
            onClick={() => setHistoryOpen(!historyOpen)}
          />
        </Row>
        <SlideOutHeight isVisible={historyOpen}>
          <HorizontalSpacer spacing={1} />
          <Small>2025-10-16 11:37</Small>
          <BodySmall>LG skickat till mottagare@mail.se</BodySmall>
          <Small color={theme.colors.textSecondary}>
            Öppnat 2025-10-16 14:10
          </Small>
        </SlideOutHeight>
      </GreyContainer>*/}

      {errorMessage && (
        <>
          <HorizontalSpacer spacing={1} />
          <Alert type="error" title={errorMessage} small />
        </>
      )}
      {successMessage && (
        <>
          <HorizontalSpacer spacing={1} />
          <Alert type="success" title={successMessage} small />
        </>
      )}
      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 24 }}>
        <DefaultButton title={t("common.cancel")} onClick={onClose} />
        <ActionButton
          color={theme.colors.primary}
          title={t("random.send")}
          onClick={handleSend}
          loading={loading}
          disabled={!recipientEmail.trim()}
        />
      </div>
    </div>
  );
};

export default SendLgModalContent;
